import { Anchor, AppShell, Badge, Box, Burger, Button, Container, Drawer, Group, Paper, Stack, Text, Title } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { useEffect, useMemo, useState } from "react"
import { apiFetch } from "atlas_forecasting_app/lib/api_fetch"
import { RouterLink, useRouter } from "atlas_forecasting_app/lib/router"
import { FeedPage } from "atlas_forecasting_app/pages/feed_page"
import { ForecastPage } from "atlas_forecasting_app/pages/forecast_page"
import { HomePage } from "atlas_forecasting_app/pages/home_page"
import { ContactPage } from "atlas_forecasting_app/pages/contact_page"
import { AdminContactMessagesPage } from "atlas_forecasting_app/pages/admin_contact_messages_page"
import { AdminFeaturedQuestionsPage } from "atlas_forecasting_app/pages/admin_featured_questions_page"
import { AdminForecastsPage } from "atlas_forecasting_app/pages/admin_forecasts_page"
import { AdminHomePage } from "atlas_forecasting_app/pages/admin_home_page"
import { AdminUserPage } from "atlas_forecasting_app/pages/admin_user_page"
import { AdminUsersPage } from "atlas_forecasting_app/pages/admin_users_page"
import { AccountBalanceHistoryPage } from "atlas_forecasting_app/pages/account_balance_history_page"
import { AccountSettingsPage } from "atlas_forecasting_app/pages/account_settings_page"
import { AddFundsPage, AddFundsReturnPage } from "atlas_forecasting_app/pages/add_funds_page"
import { DraftQuestionsPage } from "atlas_forecasting_app/pages/draft_questions_page"
import { MyQuestionsPage } from "atlas_forecasting_app/pages/my_questions_page"
import { NewQuestionPage } from "atlas_forecasting_app/pages/new_question_page"
import { NotFoundPage } from "atlas_forecasting_app/pages/not_found_page"
import { PrivacyPolicyPage } from "atlas_forecasting_app/pages/privacy_policy_page"
import { QuestionPage } from "atlas_forecasting_app/pages/question_page"
import { SignupPage } from "atlas_forecasting_app/pages/signup_page"
import { TermsOfServicePage } from "atlas_forecasting_app/pages/terms_of_service_page"
import { AuthModalButton, CenteredMessage, ErrorAlert, LoadingState } from "atlas_forecasting_app/components/common/page_elements"
import {
  IconChartDots,
  IconCirclePlus,
  IconHome2,
  IconListDetails,
  IconLogin2,
  IconShieldLock,
  IconUserCircle,
  IconWallet,
  appIcon,
} from "atlas_forecasting_app/components/common/icons"
import { formatCurrencyFromCents } from "atlas_forecasting_app/lib/format"

export function App() {
  const { path, navigate } = useRouter()
  const [currentUser, setCurrentUser] = useState(null)
  const [authInfo, setAuthInfo] = useState({ google_client_id: null, pending_signup: null })
  const [appInfo, setAppInfo] = useState({ forecast_cost_cents: 800, stripe: { enabled: false } })
  const [meLoaded, setMeLoaded] = useState(false)
  const [meError, setMeError] = useState("")
  const [drawerOpened, { open: openDrawer, close: closeDrawer, toggle: toggleDrawer }] = useDisclosure(false)

  useEffect(() => {
    let mounted = true
    apiFetch("/api/me")
      .then((data) => {
        if (!mounted) return
        setCurrentUser(data.user)
        setAuthInfo(data.auth || { google_client_id: null, pending_signup: null })
        setAppInfo(data.app || { forecast_cost_cents: 800 })
        setMeLoaded(true)
        setMeError("")
      })
      .catch((e) => {
        if (!mounted) return
        setMeLoaded(true)
        setMeError(e.message)
      })
    return () => {
      mounted = false
    }
  }, [])

  const logout = async () => {
    try {
      await apiFetch("/logout", { method: "DELETE" })
      setCurrentUser(null)
      navigate("/")
    } catch (e) {
      setMeError(e.message)
    }
  }

  const route = useMemo(() => {
    if (path === "/") return { name: "home" }
    if (path === "/contact") return { name: "contact" }
    if (path === "/privacy") return { name: "privacy" }
    if (path === "/terms") return { name: "terms" }
    if (path === "/signup") return { name: "signup" }
    if (path === "/account/settings") return { name: "account_settings" }
    if (path === "/account/balance-history") return { name: "balance_history" }
    if (path === "/account/add-funds") return { name: "add_funds" }
    if (path === "/account/add-funds/return") return { name: "add_funds_return" }
    if (path === "/feed") return { name: "feed" }
    if (path === "/my-questions") return { name: "my_questions" }
    if (path === "/draft-questions") return { name: "draft_questions" }
    if (path === "/new-question") return { name: "new_question" }
    if (path === "/admin") return { name: "admin_home" }
    if (path === "/admin/contact-messages") return { name: "admin_contact_messages" }
    if (path === "/admin/featured-questions") return { name: "admin_featured_questions" }
    if (path === "/admin/users") return { name: "admin_users" }
    if (path === "/admin/forecasts") return { name: "admin_forecasts" }

    const adm = path.match(/^\/admin\/users\/(\d+)$/)
    if (adm) return { name: "admin_user", id: adm[1] }

    const draft = path.match(/^\/draft-questions\/(\d+)$/)
    if (draft) return { name: "draft_question", id: draft[1] }

    const forecast = path.match(/^\/forecasts\/(\d+)$/)
    if (forecast) return { name: "forecast", id: forecast[1] }

    const m = path.match(/^\/questions\/(\d+)$/)
    if (m) return { name: "question", id: m[1] }

    return { name: "not_found" }
  }, [path])

  const authedOnly = ["account_settings", "balance_history", "add_funds", "add_funds_return", "feed", "my_questions", "draft_questions", "draft_question", "new_question", "admin_home", "admin_contact_messages", "admin_featured_questions", "admin_users", "admin_user", "admin_forecasts"].includes(route.name)
  const isAuthed = !!currentUser
  const isAdmin = !!currentUser?.roles?.includes("admin")
  const accountBalanceCents = currentUser?.account_balance_cents ?? 0
  const accountActive = ["account_settings", "balance_history", "add_funds", "add_funds_return"].includes(route.name)
  const balanceHistoryActive = route.name === "balance_history"
  const negativeBalance = accountBalanceCents < 0

  const renderAnonymousAuthButton = ({ mobile = false } = {}) => {
    if (!meLoaded || isAuthed) return null

    if (!authInfo?.google_client_id) return null

    return (
      <AuthModalButton
        authInfo={authInfo}
        onAuthResult={handleAuthResult}
        label="Log in"
        variant="light"
        size={mobile ? "sm" : "md"}
        modalTitle="Sign up or Log in"
        modalBody="Choose how you'd like to access Atlas Forecasting."
        leftSection={appIcon(IconLogin2, "inline")}
      />
    )
  }

  const renderBalanceBadge = (mobile = false) => {
    if (!isAuthed) return null

    return (
      <Box component={RouterLink} to="/account/balance-history" style={{ textDecoration: "none", display: "inline-flex" }} className="af-app-balance-link" title="View balance history">
        <Badge
          variant={balanceHistoryActive ? "filled" : "light"}
          color={negativeBalance ? "red" : "green"}
          size={mobile ? undefined : "lg"}
          leftSection={appIcon(IconWallet, "inline")}
        >
          {mobile ? formatCurrencyFromCents(accountBalanceCents) : `${formatCurrencyFromCents(accountBalanceCents)} balance`}
        </Badge>
      </Box>
    )
  }

  const navItems = useMemo(() => {
    const items = []

    if (isAuthed) {
      items.push({ key: "feed", label: "Feed", href: "/feed", icon: IconChartDots, active: route.name === "feed" })
      items.push({ key: "my_questions", label: "My Questions", href: "/my-questions", icon: IconListDetails, active: route.name === "my_questions" || route.name === "draft_questions" || route.name === "draft_question" })
      items.push({ key: "new_question", label: "New Question", href: "/new-question", icon: IconCirclePlus, active: route.name === "new_question" })
      items.push({ key: "account", label: "Account", href: "/account/settings", icon: IconUserCircle, active: accountActive })
    }

    if (isAuthed && isAdmin) {
      items.push({ key: "admin", label: "Admin", href: "/admin", icon: IconShieldLock, active: ["admin_home", "admin_contact_messages", "admin_featured_questions", "admin_users", "admin_user", "admin_forecasts"].includes(route.name), color: "red" })
    }

    return items
  }, [accountActive, isAuthed, isAdmin, route.name])

  const updateCurrentUser = (partialUser) => {
    setCurrentUser((prev) => (prev ? { ...prev, ...partialUser } : prev))
  }

  const handleAuthResult = (data) => {
    setCurrentUser(data?.user || null)
    setAuthInfo((prev) => ({
      ...prev,
      pending_signup: data?.pending_signup || null,
    }))

    navigate(data?.redirect_to || "/")
  }

  const handleSignupComplete = (user, redirectTo) => {
    setCurrentUser(user)
    setAuthInfo((prev) => ({ ...prev, pending_signup: null }))
    navigate(redirectTo || "/")
  }

  const renderNavigationButtons = (mobile = false) =>
    navItems.map((item) => (
      <Button
        key={item.key}
        component={RouterLink}
        to={item.href}
        variant={item.active ? "light" : "subtle"}
        color={item.color || "blue"}
        fullWidth={mobile}
        justify={mobile ? "flex-start" : "center"}
        className="af-app-nav-button"
        leftSection={appIcon(item.icon, "inline")}
        onClick={mobile ? closeDrawer : undefined}
      >
        {item.label}
      </Button>
    ))

  const renderContent = () => {
    if (!meLoaded) return <LoadingState label="Loading application..." />

    if (authedOnly && !isAuthed) {
      return (
        <CenteredMessage
          title="Log in required"
          body="Log in or create an account to continue."
          action={
            authInfo?.google_client_id ? (
              <AuthModalButton
                authInfo={authInfo}
                onAuthResult={handleAuthResult}
                label="Log in"
                modalTitle="Sign up or Log in"
                modalBody="Choose how you'd like to access Atlas Forecasting."
                leftSection={appIcon(IconLogin2, "inline")}
              />
            ) : (
              <Paper withBorder p="md" radius="md" bg="red.0">
                <Text c="red.8">Google sign-in is not configured (set GOOGLE_CLIENT_ID).</Text>
              </Paper>
            )
          }
        />
      )
    }

    if (route.name === "home") return <HomePage currentUser={currentUser} authInfo={authInfo} onAuthResult={handleAuthResult} />
    if (route.name === "contact") return <ContactPage currentUser={currentUser} />
    if (route.name === "privacy") return <PrivacyPolicyPage />
    if (route.name === "terms") return <TermsOfServicePage />
    if (route.name === "signup") return <SignupPage currentUser={currentUser} authInfo={authInfo} onSignupComplete={handleSignupComplete} onAuthResult={handleAuthResult} />
    if (route.name === "account_settings") {
      return <AccountSettingsPage currentUser={currentUser} onCurrentUserUpdate={updateCurrentUser} onLogout={logout} />
    }
    if (route.name === "balance_history") return <AccountBalanceHistoryPage onCurrentUserUpdate={updateCurrentUser} stripeEnabled={!!appInfo?.stripe?.enabled} />
    if (route.name === "add_funds") {
      return <AddFundsPage stripeConfig={appInfo?.stripe} currentBalanceCents={accountBalanceCents} onCurrentUserUpdate={updateCurrentUser} />
    }
    if (route.name === "add_funds_return") {
      return <AddFundsReturnPage currentBalanceCents={accountBalanceCents} onCurrentUserUpdate={updateCurrentUser} />
    }
    if (route.name === "feed") return <FeedPage />
    if (route.name === "forecast") return <ForecastPage forecastId={route.id} />
    if (route.name === "my_questions") return <MyQuestionsPage />
    if (route.name === "draft_questions") return <DraftQuestionsPage />

    if (route.name === "new_question") {
      return <NewQuestionPage onQuestionCreated={(id) => navigate(`/questions/${id}`)} />
    }

    if (route.name === "draft_question") {
      return <NewQuestionPage draftId={route.id} onQuestionCreated={(id) => navigate(`/questions/${id}`)} />
    }

    if (route.name === "question") {
      return (
        <QuestionPage
          questionId={route.id}
          currentUser={currentUser}
          forecastCostCents={appInfo?.forecast_cost_cents ?? 800}
          stripeEnabled={!!appInfo?.stripe?.enabled}
          onCurrentUserUpdate={updateCurrentUser}
        />
      )
    }

    if (route.name === "admin_home") return <AdminHomePage currentUser={currentUser} />
    if (route.name === "admin_contact_messages") return <AdminContactMessagesPage currentUser={currentUser} />
    if (route.name === "admin_featured_questions") return <AdminFeaturedQuestionsPage currentUser={currentUser} />
    if (route.name === "admin_users") return <AdminUsersPage currentUser={currentUser} />
    if (route.name === "admin_user") return <AdminUserPage currentUser={currentUser} userId={route.id} />
    if (route.name === "admin_forecasts") return <AdminForecastsPage currentUser={currentUser} />

    return <NotFoundPage />
  }

  return (
    <AppShell className="af-app-shell" header={{ height: { base: 90, sm: 88 } }} footer={{ height: { base: 102, sm: 72 } }} padding="lg">
      <AppShell.Header className="af-app-header">
        <Container size="xl" h="100%">
          <Group h="100%" justify="space-between" wrap="nowrap" className="af-app-header__inner">
            <Group gap="sm" wrap="nowrap">
              <Burger opened={drawerOpened} onClick={toggleDrawer} hiddenFrom="sm" aria-label="Toggle navigation" />
              <Box component={RouterLink} to="/" className="af-app-brand" style={{ textDecoration: "none", color: "inherit" }}>
                <Text className="af-app-brand__eyebrow">Forecast platform</Text>
                <Title order={3} className="af-app-brand__title">
                  Atlas Forecasting
                </Title>
              </Box>
            </Group>

            <Group gap="xs" visibleFrom="sm" wrap="nowrap">
              {renderBalanceBadge(false)}
              {renderNavigationButtons(false)}
              {renderAnonymousAuthButton({ mobile: false })}
            </Group>

            <Group gap="xs" hiddenFrom="sm" wrap="nowrap">
              {renderBalanceBadge(true)}
              {renderAnonymousAuthButton({ mobile: true })}
            </Group>
          </Group>
        </Container>
      </AppShell.Header>

      <Drawer opened={drawerOpened} onClose={closeDrawer} title="Navigation" hiddenFrom="sm" padding="md" size="sm">
        <Stack gap="sm">
          <Button
            component={RouterLink}
            to="/"
            variant={route.name === "home" ? "light" : "subtle"}
            fullWidth
            justify="flex-start"
            leftSection={appIcon(IconHome2, "inline")}
            onClick={closeDrawer}
          >
            Home
          </Button>
          {renderNavigationButtons(true)}
        </Stack>
      </Drawer>

      <AppShell.Main className="af-app-main">
        <Container size="xl">
          <Stack gap="lg">
            <ErrorAlert message={meError} />
            <Box component="main">{renderContent()}</Box>
          </Stack>
        </Container>
      </AppShell.Main>

      <AppShell.Footer className="af-app-footer">
        <Container size="xl" h="100%">
          <Group h="100%" justify="space-between" wrap="wrap" gap="sm" className="af-app-footer__inner">
            <Stack gap={2}>
              <Text size="sm" fw={600} className="af-app-footer__title">
                Atlas Forecasting
              </Text>
              <Text size="sm" className="af-app-footer__copy">
                Structured questions, probabilistic forecasts, and secure Atlas balance funding through Stripe.
              </Text>
            </Stack>
            <Group gap="md" wrap="wrap" className="af-app-footer__links">
              <Anchor component={RouterLink} to="/contact" size="sm" c="dimmed" underline="hover">
                Contact Us
              </Anchor>
              <Anchor component={RouterLink} to="/terms" size="sm" c="dimmed" underline="hover">
                Terms of Service
              </Anchor>
              <Anchor component={RouterLink} to="/privacy" size="sm" c="dimmed" underline="hover">
                Privacy Policy
              </Anchor>
            </Group>
          </Group>
        </Container>
      </AppShell.Footer>
    </AppShell>
  )
}
